import { ENV } from '@app/constants/env';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CustomerEntity, ExhibitorListRequest, ShowDetails, ExhibitorShowSaveResponse, ExhibitorShowSaveDto, ExhibitorShowEntity, UserExhibitorShowEntity, UserDetailsByIdDto } from '@feature/types/exhibitor.types';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getToken } from '../../../src/helpers/authProvider';

// Helper function to map API response (lowercase) to interface format (camelCase)
const mapCustomerResponse = (item: any): CustomerEntity => ({
    mstr_customer_id: item.mstr_customer_id,
    create_Date: item.create_date,
    create_User: item.create_user,
    edit_Date: item.edit_date,
    edit_User: item.edit_user,
    createdBy: item.createdby,
    name: item.name,
    address: item.address,
    countryId: item.countryid,
    stateID: item.stateid,
    cityID: item.cityid,
    pin: item.pin,
    email: item.email,
    phone: item.phone,
    warehousing: item.warehousing,
    vatNumber: item.vatnumber,
    countryName: item.countryname,
    stateName: item.statename,
    cityName: item.cityname,
    mstr_Show_ID: item.mstr_show_id,
    showcode: item.showcode,
    contact_Name: item.contact_name,
    contact_LName: item.contact_lname,
    contact_Email: item.contact_email,
    agent_Name: item.agent_name,
    agent_GEMSCode: item.agent_gemscode,
    contact_Phone: item.contact_phone,
    agent_Email: item.agent_email,
    agent_Phone: item.agent_phone,
    gemscustcode: item.gemscustcode,
    notification: item.notification,
    detailsent: item.detailsent,
    paymentdetailsent: item.paymentdetailsent,
    totalRecord: item.totalrecord,
    row_Number: item.row_number,
    hallNo: item.hallno,
    standNo: item.standno,
    internalID: item.internalid,
    isWOCreated: item.iswocreated === 1
});

const rawBaseQuery = fetchBaseQuery({
    baseUrl: ENV.BASE_API_URL +ENV.MASTERS_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    incomingArgs,
    api,
    extraOptions
) => {
    let args: string | FetchArgs = incomingArgs;

    // normalize string arg to object so we can set headers
    if (typeof args === 'string') {
        args = { url: args };
    }

    try {
        const token = await getToken();
        if (token) {
            // Check if body is FormData - if so, don't create new Headers object
            // because it will lose the multipart boundary
            if ((args as FetchArgs).body instanceof FormData) {
                // For FormData, just add Authorization to existing headers or create minimal headers
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${token}`);
                (args as FetchArgs).headers = headers;
                // Don't set Content-Type - browser will set it with boundary
            } else {
                const headers =
                    (args as FetchArgs).headers instanceof Headers
                        ? (args as FetchArgs).headers
                        : new Headers((args as FetchArgs).headers);

                headers.set('Authorization', `Bearer ${token}`);
                (args as FetchArgs).headers = headers;
            }
        }

        const result = await rawBaseQuery(args, api, extraOptions);


        if (result.error && result.error.status === 401 && token) {
            // Handle token refresh if needed
        }

        return result;
    } catch {
        return { error: { status: 500, data: 'Token retrieval failed' } } as any;
    }
};

export const exhibitorApi = createApi({
    reducerPath: 'exhibitorApi',
    baseQuery: authBaseQuery,
    tagTypes: ['Exhibitor', 'Shows'],
    endpoints: (builder) => ({
        getShowDetailsByUser: builder.query<ShowDetails[], string>({
            query: (username) => ({
                url: `Customer/GetShowDetailsByUser/${username}`,
                method: 'GET'
            }),
            providesTags: ['Shows'],
        }), getExhibitors: builder.query<CustomerEntity[], ExhibitorListRequest>({
            query: (params) => ({
                url: 'Customer/GetAllExhibitorDetails',
                method: 'POST',
                body: {
                    userID: params.UserID,
                    showId: params.Mstr_Show_ID.toString(),
                    pageNumber: params.PageNumber,
                    pageSize: params.PageSize,
                    totalRecord: params.TotalRecord || 0,
                    filtered: params.filtered || "",
                    sortColumn: params.SortColumn || "mstr_customer_id",
                    sortOrder: params.SortOrder || "desc"
                },
            }),
            transformResponse: (response: any[]): CustomerEntity[] => {
                // Map API response array using helper function
                return response.map(mapCustomerResponse);
            },
            providesTags: ['Exhibitor'],
        }), getExhibitorById: builder.query<CustomerEntity, { id: number; showId: number; userId: number }>({
            query: ({ id, showId, userId }) => {
                return {
                    url: 'Customer/GetAllExhibitorDetails',
                    method: 'POST',
                    body: {
                        userID: userId,
                        showId: showId.toString(),
                        pageNumber: 1,
                        pageSize: 1000, // Large enough to get all records
                        totalRecord: 0,
                        filtered: `mstr_customer_id,IsEqualTo,${id}`, // Filter by customer ID
                        sortColumn: "mstr_customer_id",
                        sortOrder: "desc"
                    },
                };
            }, transformResponse: (response: any[], meta, arg): CustomerEntity => {
                if (!Array.isArray(response)) {
                    throw new Error('Invalid response format - expected array');
                }

                // Find the specific customer from the response
                const item = response.find(c => c.mstr_customer_id === arg.id);
                if (!item) {
                    throw new Error(`Customer with ID ${arg.id} not found`);
                }

                // Map using helper function
                return mapCustomerResponse(item);
            },
            providesTags: (result, error, { id }) => [{ type: 'Exhibitor', id }],
        }),
        createExhibitor: builder.mutation<{ createddata: CustomerEntity }, { exhibitor: Partial<CustomerEntity>; username: string }>({
            query: ({ exhibitor, username }) => ({
                url: `Customer/CreateCustomer/${username}`,
                method: 'POST',
                body: {
                    name: exhibitor.name,
                    address: exhibitor.address,
                    countryId: exhibitor.countryId,
                    stateId: exhibitor.stateID,
                    cityID: exhibitor.cityID,
                    pin: exhibitor.pin,
                    email: exhibitor.email,
                    phone: exhibitor.phone,
                    userId: exhibitor.userId,
                    warehousing: exhibitor.warehousing || false,
                    vatNumber: exhibitor.vatNumber,
                    mstr_Show_ID: exhibitor.mstr_Show_ID,
                    contact_Name: exhibitor.contact_Name,
                    contact_LName: exhibitor.contact_LName,
                    contact_Email: exhibitor.contact_Email,
                    agent_Name: exhibitor.agent_Name,
                    agent_Phone: exhibitor.agent_Phone,
                    agent_GEMSCode: exhibitor.agent_GEMSCode,
                    contact_Phone: exhibitor.contact_Phone,
                    agent_Email: exhibitor.agent_Email,
                    countryName: exhibitor.countryName,
                    cityName: exhibitor.cityName,
                    stateName: exhibitor.stateName,
                    hallNo: exhibitor.hallNo,
                    standNo: exhibitor.standNo,
                    internalID: exhibitor.internalID,
                    uc_Userid: exhibitor.uc_Userid,
                    uc_Showid: exhibitor.uc_Showid,
                    uc_Countryid: exhibitor.uc_Countryid
                },
            }),
            transformResponse: (response: any) => {
                // Transform the response data using helper function
                if (response.createddata) {
                    return {
                        createddata: mapCustomerResponse(response.createddata)
                    };
                }
                return response;
            },
            invalidatesTags: ['Exhibitor'],
        }),
        updateExhibitor: builder.mutation<{ success: boolean; message: string; data: CustomerEntity }, { exhibitor: Partial<CustomerEntity>; username: string }>({
            query: ({ exhibitor, username }) => {
                const requestBody = {
                    Name: exhibitor.name,
                    Address: exhibitor.address,
                    CountryId: exhibitor.countryId,
                    StateId: exhibitor.stateID,
                    CityId: exhibitor.cityID,
                    Pin: exhibitor.pin || "",
                    Email: exhibitor.email,
                    Phone: exhibitor.phone,
                    GemsCustCode: exhibitor.gemscustcode || "",
                    Warehousing: exhibitor.warehousing || false,
                    VatNumber: exhibitor.vatNumber,
                    Mstr_Show_Id: exhibitor.mstr_Show_ID || 0,
                    Contact_Name: exhibitor.contact_Name,
                    Contact_LName: exhibitor.contact_LName,
                    Contact_Email: exhibitor.contact_Email,
                    Agent_Name: exhibitor.agent_Name || "",
                    Agent_Phone: exhibitor.agent_Phone || "",
                    Agent_GEMSCode: exhibitor.agent_GEMSCode || "",
                    Contact_Phone: exhibitor.contact_Phone || "",
                    Agent_Email: exhibitor.agent_Email || "",
                    CountryName: exhibitor.countryName || "",
                    CityName: exhibitor.cityName || "",
                    StateName: exhibitor.stateName || "",
                    HallNo: exhibitor.hallNo || "",
                    StandNo: exhibitor.standNo || "",
                    InternalId: exhibitor.internalID || 0
                };

                console.log('UpdateCustomer Request:', {
                    url: `Customer/UpdateCustomer/${exhibitor.mstr_customer_id}/${encodeURIComponent(username)}`,
                    body: requestBody
                });

                return {
                    url: `Customer/UpdateCustomer/${exhibitor.mstr_customer_id}/${encodeURIComponent(username)}`,
                    method: 'PUT',
                    body: requestBody,
                };
            },
            transformResponse: (response: any) => {
                // Transform the response data using helper function
                if (response.success && response.data) {
                    return {
                        success: response.success,
                        message: response.message,
                        data: mapCustomerResponse(response.data)
                    };
                }
                return response;
            },
            invalidatesTags: ['Exhibitor'],
        }), saveExhibitorShow: builder.mutation<ExhibitorShowSaveResponse, ExhibitorShowSaveDto>({
            query: (request) => ({
                url: 'Customer/SaveExhibitorShow',
                method: 'POST',
                body: request
            }),
            invalidatesTags: ['Shows', 'Exhibitor']
        }), getCustomerById: builder.query<CustomerEntity, number>({
            query: (id) => ({
                url: `Customer/GetCustomerById/${id}`,
                method: 'GET'
            }),
            transformResponse: (response: any): CustomerEntity => {
                // Map using helper function
                return mapCustomerResponse(response);
            },
            providesTags: (result, error, id) => [{ type: 'Exhibitor', id }]
        }),
        getExhibitorShows: builder.query<ExhibitorShowEntity[], number>({
            query: (custId) => ({
                url: `Customer/GetExibitorShow/${custId}`,
                method: 'GET'
            }),
            providesTags: ['Shows']
        }),
        getUserExhibitorShows: builder.query<UserExhibitorShowEntity[], number>({
            query: (custId) => ({
                url: `Customer/GetUsersByCustomer/${custId}`,
                method: 'GET'
            }),
            providesTags: ['Shows']
        }),
        getUserByIdForNewCust: builder.query<CustomerEntity[], { dto: UserDetailsByIdDto }>({
            query: ({ dto }) => ({
                url: `${ENV.USER_APIURL}/User/GetUserByIdForNewCust`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: dto,
            }),
            providesTags: ['Exhibitor'],
        }),
    }),
});

export const {
    useGetExhibitorsQuery,
    useGetExhibitorByIdQuery,
    useGetCustomerByIdQuery,
    useCreateExhibitorMutation,
    useUpdateExhibitorMutation,
    useGetShowDetailsByUserQuery,
    useSaveExhibitorShowMutation,
    useGetExhibitorShowsQuery,
    useGetUserExhibitorShowsQuery,
    useGetUserByIdForNewCustQuery,

} = exhibitorApi;
